import React, { useEffect } from 'react';

const UserTable = ({ 
  users, 
  onEdit, 
  onCloneUser,
  onDelete, 
  onToggleStatus, 
  getEspecialistaName,
  onSort,
  sortConfig,
  onManageDomains
}) => {
  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const expiryDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 0 && diffDays <= 30;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const expiryDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expiryDate < today;
  };

  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <i className="fas fa-sort text-gray-300 dark:text-slate-600 ml-1 opacity-0 group-hover:opacity-100 transition"></i>;
    return (
      <i className={`fas ${sortConfig.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down'} text-cyan-600 dark:text-cyan-400 ml-1`}></i>
    );
  };

  const Header = ({ label, columnKey, className = "" }) => (
    <th 
      className={`px-6 py-4 text-xs font-bold text-gray-500 dark:text-slate-300 uppercase tracking-wider cursor-pointer group hover:bg-gray-100/50 dark:hover:bg-slate-800 transition-colors ${className}`}
      onClick={() => onSort(columnKey)}
    >
      <div className="flex items-center">
        {label}
        <SortIndicator columnKey={columnKey} />
      </div>
    </th>
  );

  const getNormalizedDomains = (dominios) => {
    if (!dominios) return [];
    if (Array.isArray(dominios)) {
      return dominios.map(d => String(d["dominio"]).trim()).filter(Boolean);
    }
    if (typeof dominios === 'string') {
      const trimmed = dominios.trim();
      if (trimmed) {
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              return parsed.map(d => String(d).trim()).filter(Boolean);
            }
          } catch {}
        }
        return trimmed.split(',').map(d => d.trim()).filter(Boolean);
      }
    }
    return [];
  };

  return (
    <div className="overflow-x-auto bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 transition-colors">
      <table className="w-full text-left border-collapse">
        <thead className="bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-100 dark:border-slate-800">
          <tr>
            <Header label="Usuario" columnKey="nombre" />
            <Header label="Rol" columnKey="rol" />
            <Header label="Estado" columnKey="activo" />
            <Header label="Vencimiento" columnKey="vence_en" />
            <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-slate-300 uppercase tracking-wider">Cliente/Ejecutivo</th>
            <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-slate-300 uppercase tracking-wider text-right">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
          {users.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-6 py-12 text-center text-gray-400 dark:text-slate-500">
                <i className="fas fa-search text-4xl mb-3 block"></i>
                No se encontraron usuarios que coincidan con la búsqueda.
              </td>
            </tr>
          ) : (
            users.map((user) => {
              const expiringSoon = user.rol === 'cliente' && isExpiringSoon(user.vence_en);
              const expired = user.rol === 'cliente' && isExpired(user.vence_en);
              const normDomains = user.rol === 'cliente' ? getNormalizedDomains(user.dominios) : [];

              return (
                <tr key={user.id} className="hover:bg-cyan-50/30 dark:hover:bg-cyan-400/5 transition group">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold text-sm mr-3">
                        {user.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-800 dark:text-slate-100">{user.nombre}</div>
                        <div className="text-xs text-gray-500 dark:text-slate-300">{user.correo}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                      user.rol === 'especialista' 
                        ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-300' 
                        : 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-300'
                    }`}>
                      {user.rol}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => onToggleStatus(user.id)}
                      className={`flex items-center space-x-1.5 px-3 py-1 rounded-full transition ${
                      user.activo === '1' 
                        ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-500/30' 
                        : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-500/30'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${user.activo === '1' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <span className="text-xs font-bold">{user.activo === '1' ? 'Activo' : 'Inactivo'}</span>
                    </button>
                  </td>
                  <td className="px-6 py-4">
                     <div className={`text-sm flex items-center px-2 py-1 rounded-lg w-fit ${
                      expiringSoon ? 'bg-orange-50 dark:bg-orange-400/20 text-orange-600 dark:text-orange-300 font-bold border border-orange-100 dark:border-orange-400/30 animate-pulse' : 
                      expired || (user.vence_en === "None" && user.rol === 'cliente')  ? 'bg-red-50 dark:bg-red-400/20 text-red-600 dark:text-red-300 font-bold border border-red-100 dark:border-red-400/30' : 
                      'text-gray-600 dark:text-slate-200'
                    }`}>
                      {user.rol === 'cliente' ? (
                        <>
                          <i className={`far fa-calendar-alt mr-2 ${expiringSoon ? 'text-orange-500' : expired || user.vence_en === "None" ? 'text-red-500' : 'text-cyan-500'}`}></i>
                          {user.vence_en === "None" ? "[Sin fecha]" : user.vence_en }
                          {(expiringSoon || user.vence_en === "None" )&& <i className="fas fa-exclamation-triangle ml-2 text-[10px]"></i>}
                        </>
                      ) : (
                        <span className="text-gray-300 dark:text-slate-500 italic font-medium">No aplica</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.rol === 'cliente' ? (
                      <div>
                        <div className="text-xs font-bold text-gray-700 dark:text-slate-200">Ref: {user.numcliente || 'N/A'}</div>
                        <div className="text-[10px] text-gray-500 dark:text-slate-400">Ejecutivo: {getEspecialistaName(user.id_ejecutivo)}</div>
                      </div>
                    ) : (
                      <div className="text-xs font-bold text-gray-700 dark:text-slate-200">ID: {user.numcliente || user.id}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end items-center space-x-1">
                      {user.rol === 'cliente' && (
                        <button
                          onClick={() => onManageDomains(user)}
                          className="p-2 text-gray-400 dark:text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition"
                          title="Gestionar Dominios"
                        >
                          <i className="fas fa-globe"></i>
                        </button>
                      )}
                      {user.rol === 'cliente' && onCloneUser && (
                        <button
                          onClick={() => onCloneUser(user)}
                          className="p-2 text-gray-400 dark:text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition"
                          title="Usar como plantilla para nuevo usuario"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(user)}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition"
                        title="Editar Usuario"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;
